// The keyboard's way through the feed (dioxus-plan.md D382).
//
// ArrowUp from the very top of the composer's box takes the focus to the
// newest message drawn; on a message the arrows step to its neighbours,
// ArrowDown off the newest one and Escape go back to the box, and E edits it.
// Which key does what — and whether a message may be edited at all — is each
// client's own handler's to decide, beside the composer's Enter and Escape.
// What is here is the part that is the same DOM in both clients (D57): which
// element is «the message above», how it comes to take the focus, and how far
// the column moves to show it.
//
//     window.__echoFeedKeys.last()   // focus the newest message; false if none
//     window.__echoFeedKeys.step(d)  // -1 older, +1 newer, from the focused one
//     window.__echoFeedKeys.box()    // back to the composer's box
//     window.__echoFeedKeys.enter()  // into the focused message; false if it
//                                    // has nothing to stop at
//
// Tab or → on a message goes into it (docs/tooltips-plan.md D752, D805 —
// Enter opens its menu and Space picks it now, D804): the focus walks its
// stops — every control inside it, in their order, and last its author's
// profile — with Tab and the side arrows, around the ends; the up and down
// arrows leave for the message before or after, and Escape goes back to the
// message. Those keys are the same in both clients to the last detail, so
// they are read here, on `document`, ahead of either client's own handler; a
// stop that is neither a button nor a link is pressed with `click()`. The
// profile is found by markup rather than by class: `data-feed-profile` on the
// face and the name, inside the message's `data-feed-group`.
//
// A message can take the focus only while it holds it. A `tabindex` on every
// bubble would let a click focus one as well, and a clicked message would then
// take the arrows that scroll the feed today and step through messages with
// them instead. So the attribute goes on as the walk arrives and comes off as
// the focus leaves, and the pointer, Tab and the arrows after a click do what
// they always did. Not when it is the whole window that loses the focus: the
// message stays the document's active element through that, and an attribute
// taken off then would let the engine's focus fixup drop it on <body> before
// the reader is back.
//
// The column is moved by hand rather than by `scrollIntoView`. In the wide
// layout the header and the composer float over the feed and the scroller pays
// them back as padding (`.chat-feed__scroll` in app.css), so a message parked
// under the composer is «in view» to the engine and behind glass to the
// reader. The band a message has to stand in is the scroller's box less its
// padding.
//
// Moving the column *up* is the reader's own hand, like a wheel turned upwards
// (`handoff_script` in the desktop's message.rs, `takeOver` in the web's
// feed.tsx): the follow of an arrival that is still settling would otherwise
// pull them back down to the bottom. So before such a move the scroller hears
// `echo-take`, which each client answers the way it answers that wheel.
//
// Shared by both clients for the reason `ink.js` is: the desktop inlines it
// into the head of its window, the web imports it at the entry.
(function () {
  "use strict";

  if (typeof window === "undefined" || window.__echoFeedKeys) return;

  var FEED = "feed-scroll";
  var MESSAGE = ".message-bubble";
  var BOX = ".composer__input";
  // D752: what a message's stops are — what can be pressed inside it — and
  // its author's profile, which stands outside it, in its group.
  var STOPS =
    "a[href], button, input:not([type=hidden]), select, textarea, [role=button], [role=link], " +
    "[role=checkbox], [role=switch], [role=slider], [role=option], [tabindex]:not([tabindex='-1'])";
  var GROUP = "[data-feed-group]";
  var PROFILE = "[data-feed-profile]";
  var SLIDER = "input[type=range], [role=slider]";
  // A modal a message's own key opens — the question before deleting it, the
  // forward's picker (docs/tooltips-plan.md D805) — and gives the focus back
  // to it as it closes.
  var MODAL = "[role=dialog], [aria-modal=true], .echo-scrim";

  // The message the walk went into (D752): its profile, outside it, knows
  // its message only from here.
  var inside = null;

  function messages(scroller) {
    return scroller ? Array.prototype.slice.call(scroller.querySelectorAll(MESSAGE)) : [];
  }

  // Off again once the focus has gone somewhere else in the page — but not
  // into a modal, which gives it back as it closes: a message that could no
  // longer take it would send it to whatever the engine found instead.
  function release(e) {
    if (!document.hasFocus()) return;
    var to = e.relatedTarget;
    if (to instanceof Element && to.closest(MODAL) !== null) return;
    e.currentTarget.removeEventListener("blur", release);
    e.currentTarget.removeAttribute("tabindex");
  }

  function take(scroller) {
    scroller.dispatchEvent(new Event("echo-take"));
  }

  // The least move that puts el inside the band; a message taller than the
  // band shows its top, where reading it starts.
  function show(scroller, el) {
    var style = getComputedStyle(scroller);
    var box = scroller.getBoundingClientRect();
    var top = box.top + (parseFloat(style.paddingTop) || 0);
    var bottom = box.bottom - (parseFloat(style.paddingBottom) || 0);
    var rect = el.getBoundingClientRect();
    var by = 0;
    if (rect.top < top || rect.height > bottom - top) by = rect.top - top;
    else if (rect.bottom > bottom) by = rect.bottom - bottom;
    if (by < 0) take(scroller);
    if (by) scroller.scrollTop += by;
  }

  function land(el) {
    inside = null;
    if (!el.hasAttribute("tabindex")) {
      el.setAttribute("tabindex", "-1");
      el.addEventListener("blur", release);
    }
    el.focus({ preventScroll: true });
    var scroller = document.getElementById(FEED);
    if (scroller) show(scroller, el);
    return true;
  }

  function box() {
    var el = document.querySelector(BOX);
    if (!el) return false;
    el.focus();
    return true;
  }

  function last() {
    var all = messages(document.getElementById(FEED));
    return all.length > 0 && land(all[all.length - 1]);
  }

  function step(d) {
    var focused = document.activeElement;
    return stepFrom(focused && focused.closest ? focused.closest(MESSAGE) : null, d);
  }

  function stepFrom(here, d) {
    var scroller = document.getElementById(FEED);
    var all = messages(scroller);
    var at = here ? all.indexOf(here) : -1;
    if (at < 0) return false;
    var next = all[at + d];
    if (next) return land(next);
    // Past the newest: back where the walk came in.
    if (d > 0) return box();
    // Past the oldest drawn: the column goes to its top, and the scroll is
    // what asks for more above — the next press finds it there.
    if (scroller.scrollTop > 0) {
      take(scroller);
      scroller.scrollTop = 0;
    }
    return false;
  }

  // ── Inside a message (D752) ──

  function shown(el) {
    if (el.getClientRects().length === 0) return false;
    if (el.closest("[hidden], [inert], [data-keytip-actions]") !== null) return false;
    return getComputedStyle(el).visibility !== "hidden";
  }

  // What nothing covers — no sheet, no menu's dismiss layer: a key on what is
  // under one is that one's.
  function onTop(el) {
    // The middle of what of it is in the window: a message taller than the
    // feed is walked onto at its top.
    var r = el.getBoundingClientRect();
    var left = Math.max(r.left, 0);
    var right = Math.min(r.right, window.innerWidth);
    var top = Math.max(r.top, 0);
    var bottom = Math.min(r.bottom, window.innerHeight);
    if (right <= left || bottom <= top) return false;
    var hit = document.elementFromPoint((left + right) / 2, (top + bottom) / 2);
    return hit !== null && (hit === el || el.contains(hit));
  }

  function profileOf(message) {
    var group = message.closest(GROUP);
    var profile = group === null ? null : group.querySelector(PROFILE);
    return profile !== null && shown(profile) ? profile : null;
  }

  // Every control inside the message, in their order — one for a control
  // inside another, the outer — and last its author's profile.
  function stopsOf(message) {
    var out = [];
    var all = message.querySelectorAll(STOPS);
    for (var i = 0; i < all.length; i++) {
      var el = all[i];
      if (el.disabled === true || el.getAttribute("aria-disabled") === "true" || !shown(el)) continue;
      var outer = el.parentElement === null ? null : el.parentElement.closest(STOPS);
      if (outer !== null && outer !== message && message.contains(outer)) continue;
      out.push(el);
    }
    var profile = profileOf(message);
    if (profile !== null) out.push(profile);
    return out;
  }

  function go(el) {
    // A `role="button"` span with no `tabindex` takes the focus only for as
    // long as it holds it, as a message does.
    if (!el.hasAttribute("tabindex") && !el.matches("a[href], button, input, select, textarea")) {
      el.setAttribute("tabindex", "-1");
      el.addEventListener("blur", release);
    }
    el.focus({ preventScroll: true });
    var scroller = document.getElementById(FEED);
    if (scroller) show(scroller, el);
  }

  function enter() {
    var message = document.activeElement;
    if (!message || !message.matches || !message.matches(MESSAGE)) return false;
    var stops = stopsOf(message);
    if (stops.length === 0) return false;
    go(stops[0]);
    inside = message;
    return true;
  }

  function around(i, n) {
    return ((i % n) + n) % n;
  }

  function stop(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  document.addEventListener(
    "keydown",
    function (e) {
      if (e.defaultPrevented || e.isComposing || e.ctrlKey || e.altKey || e.metaKey) return;
      var t = e.target;
      var scroller = document.getElementById(FEED);
      if (!(t instanceof Element) || scroller === null || !scroller.contains(t) || !onTop(t)) return;
      var message = t.closest(MESSAGE);
      if (message === t) {
        if ((e.key === "Tab" || e.key === "ArrowRight") && !e.shiftKey && enter()) stop(e);
        return;
      }
      // A stop of the message the walk went into: inside it, or its profile.
      if (inside === null || !inside.isConnected) return;
      if (message !== inside && !(message === null && profileOf(inside) === t.closest(PROFILE))) return;
      var stops = stopsOf(inside);
      var at = -1;
      for (var i = 0; i < stops.length; i++) {
        if (stops[i] === t || stops[i].contains(t)) {
          at = i;
          break;
        }
      }
      if (at < 0) return;
      var here = inside;
      var slider = t.matches(SLIDER);
      if (e.key === "Tab") {
        stop(e);
        go(stops[around(at + (e.shiftKey ? -1 : 1), stops.length)]);
      } else if (e.shiftKey) {
        return;
      } else if ((e.key === "ArrowRight" || e.key === "ArrowLeft") && !slider) {
        stop(e);
        go(stops[around(at + (e.key === "ArrowRight" ? 1 : -1), stops.length)]);
      } else if ((e.key === "ArrowUp" || e.key === "ArrowDown") && !slider) {
        stop(e);
        stepFrom(here, e.key === "ArrowUp" ? -1 : 1);
      } else if (e.key === "Escape") {
        stop(e);
        land(here);
      } else if ((e.key === "Enter" || e.key === " ") && !e.repeat) {
        // A button presses itself on both, a link on Enter; the rest — a
        // `role="button"` span, a link on Space — is pressed here.
        var native = t.matches("button, input, select, textarea") || (e.key === "Enter" && t.matches("a[href]"));
        if (!native) {
          stop(e);
          t.click();
        }
      }
    },
    true,
  );

  window.__echoFeedKeys = { last: last, step: step, box: box, enter: enter };
})();
